import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BrandLogo } from "@/components/Logo";
import { Spinner } from "@/components/ui";
import { TextInput } from "@/components/fields";

export function Login() {
  const { user, login, refresh } = useAuth();
  const nav = useNavigate();
  const { data: status, isLoading } = useQuery({
    queryKey: ["meta-status"],
    queryFn: () => api.get<{ appName: string; needsBootstrap: boolean; emailConfigured: boolean }>("/meta/status"),
  });

  const [form, setForm] = useState({ username: "", password: "", totp: "" });
  const [mfa, setMfa] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState<"login" | "forgot">("login");
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => { if (user) nav("/", { replace: true }); }, [user, nav]);

  if (isLoading) return <CenteredSpinner />;
  if (status?.needsBootstrap) return <Bootstrap onDone={() => nav("/")} />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const res = await login(form.username, form.password, mfa ? form.totp : undefined);
      if (res.mfaRequired) { setMfa(true); setBusy(false); return; }
      await refresh();
      nav("/", { replace: true });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Sign-in failed");
      setBusy(false);
    }
  };

  const sendForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/auth/forgot-password", { email: form.username.includes("@") ? form.username : "" });
      setForgotSent(true);
    } finally { setBusy(false); }
  };

  return (
    <Shell tagline="Multi-project management & Gantt tracking">
      {mode === "login" ? (
        <form onSubmit={submit} className="space-y-4">
          <h1 className="font-serif text-xl font-semibold">Sign in</h1>
          {err && <div className="rounded-lg bg-st-delayed/10 px-3 py-2 text-sm text-st-delayed">{err}</div>}
          {!mfa ? (
            <>
              <label className="block">
                <span className="label">Username or email</span>
                <TextInput autoFocus autoComplete="username" value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })} required />
              </label>
              <label className="block">
                <span className="label">Password</span>
                <TextInput type="password" autoComplete="current-password" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} required />
              </label>
            </>
          ) : (
            <label className="block">
              <span className="label">Authentication code</span>
              <TextInput autoFocus inputMode="numeric" pattern="[0-9]*" maxLength={6} placeholder="6-digit code"
                value={form.totp} onChange={(e) => setForm({ ...form, totp: e.target.value.replace(/\D/g, "") })} required />
              <span className="mt-1 block text-[11px] text-ink-faint">Open your authenticator app for the current code.</span>
            </label>
          )}
          <button className="btn-primary w-full" disabled={busy}>{busy ? <Spinner /> : mfa ? "Verify" : "Sign in"}</button>
          {!mfa && (
            <button type="button" className="w-full text-center text-xs text-ink-faint hover:text-brand-600"
              onClick={() => { setMode("forgot"); setErr(""); }}>
              Forgot your password?
            </button>
          )}
        </form>
      ) : (
        <form onSubmit={sendForgot} className="space-y-4">
          <h1 className="font-serif text-xl font-semibold">Reset password</h1>
          {forgotSent ? (
            <p className="text-sm text-ink-soft">
              If an account exists for that email {status?.emailConfigured ? "you'll receive a reset link shortly." : "an administrator can issue you a temporary password — email is not enabled on this instance."}
            </p>
          ) : (
            <>
              <p className="text-sm text-ink-soft">Enter your account email and we'll send a reset link{status?.emailConfigured ? "" : " (if email is configured)"}.</p>
              <TextInput type="email" placeholder="you@thelongevityhealth.com" value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })} required />
              <button className="btn-primary w-full" disabled={busy}>{busy ? <Spinner /> : "Send reset link"}</button>
            </>
          )}
          <button type="button" className="w-full text-center text-xs text-ink-faint hover:text-brand-600" onClick={() => { setMode("login"); setForgotSent(false); }}>
            Back to sign in
          </button>
        </form>
      )}
    </Shell>
  );
}

function Bootstrap({ onDone }: { onDone: () => void }) {
  const { refresh } = useAuth();
  const [f, setF] = useState({ fullName: "", username: "", email: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (f.password !== f.confirm) { setErr("Passwords do not match"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/meta/bootstrap", f);
      await refresh();
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Setup failed");
      setBusy(false);
    }
  };
  return (
    <Shell tagline="First-run setup">
      <form onSubmit={submit} className="space-y-3.5">
        <h1 className="font-serif text-xl font-semibold">Create the Super Admin</h1>
        <p className="text-sm text-ink-soft">This one-time step creates the first account with full control of the platform.</p>
        {err && <div className="rounded-lg bg-st-delayed/10 px-3 py-2 text-sm text-st-delayed">{err}</div>}
        {([["fullName", "Full name", "text"], ["username", "Username", "text"], ["email", "Email", "email"], ["password", "Password (min 10 chars)", "password"], ["confirm", "Confirm password", "password"]] as const).map(([k, label, type]) => (
          <label key={k} className="block">
            <span className="label">{label}</span>
            <TextInput type={type} value={(f as any)[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} required minLength={type === "password" ? 10 : undefined} />
          </label>
        ))}
        <button className="btn-primary w-full" disabled={busy}>{busy ? <Spinner /> : "Complete setup"}</button>
      </form>
    </Shell>
  );
}

function Shell({ children, tagline }: { children: React.ReactNode; tagline: string }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <BrandLogo height={72} />
          <p className="mt-3 font-serif text-lg font-semibold">Project Management &amp; Gantt Tracker</p>
          <p className="mt-1 text-sm text-ink-soft">{tagline}</p>
        </div>
        <div className="card p-6">{children}</div>
        <p className="mt-4 text-center text-[11px] text-ink-faint">
          Authorised users only. Activity on this system is logged.
        </p>
        <p className="mt-1 text-center text-[11px] text-ink-faint"><Link to="/reset-password" className="hover:underline">Have a reset link?</Link></p>
      </div>
    </div>
  );
}

function CenteredSpinner() {
  return <div className="flex min-h-dvh items-center justify-center"><Spinner className="h-6 w-6" /></div>;
}

export { CenteredSpinner };
